import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import TextBox from "../../common/TextBox/index.jsx";
import TextArea from "../../common/TextArea/index.jsx";
import { Form, Formik } from "formik";
import Button from "../../common/Button/index.jsx";
import ProfileContext from "../ProfileContext.jsx";

// * SVGs
import { ReactComponent as ShuffleSvg } from "../../../assets/icons/shuffle.svg";
import { PAGE_STATE } from "../../../constants/index.jsx";

import useUsers from "../../../hooks/useUsers.js";
import { toast } from "react-toastify";
import AppContext from "../../../AppContext.jsx";
import { langComponent } from "../../../constants/index.jsx";
import { useNavigate } from "react-router-dom";
import { isValidNumber } from "../../../helpers/common.helper.js";

import ModalUpload from "./UploadModal.jsx";
import useCompanies from "../../../hooks/useCompanies.js";

const assetsUri = import.meta.env.ASSETS_URI;

const EditCompany = () => {
  const { company, currentPlan, setUser } = useContext(ProfileContext);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [avatar, setAvatar] = useState(undefined);
  const [toggleModal, setToggleModal] = useState(false);
  const [isAvatarChanged, setIsAvatarChanged] = useState(false);
  const { patchCompany, uploadLogo } = useCompanies();
  const {
    setUser: appSetUser,
    getLang,
    plans,
    refetchUser,
  } = useContext(AppContext);
  const Translation = getLang(langComponent.profile);
  const TranslationCommon = getLang(langComponent.common);
  const navigate = useNavigate();

  useEffect(() => {
    // if (!user || !currentPlan) return;

    if (!company) return;

    setPageState(PAGE_STATE.IDLE);
    setAvatar(company.logo);
  }, [company]);

  // * Handle avatar upload
  const handleFileChange = async (files) => {
    setToggleModal(false);

    if (!files || files === undefined) return;

    const result = await uploadLogo(company._id, files);

    setAvatar(result.logo);

    setIsAvatarChanged(true);

    toast.success("Logo saved");
  };

  const handleViewClose = () => {
    setToggleModal(false);
  };

  const handleCancelClick = () => {
    navigate("/profile");
  };

  const handleValidate = (values) => {
    const {
      firstname,
      surname,
      bio,
      streetAddress,
      apt,
      subDistrict,
      district,
      province,
      postalCode,
    } = values;

    let errors = {};

    // * Validate address section if only a field in the section is specified
    if (
      streetAddress ||
      apt ||
      subDistrict ||
      district ||
      province ||
      postalCode
    ) {
      // * Leave out apt since it's optional
      if (!streetAddress) errors.streetAddress = "Cannot be blank";
      if (!subDistrict) errors.subDistrict = "Cannot be blank";
      if (!district) errors.district = "Cannot be blank";
      if (!province) errors.province = "Cannot be blank";
      if (!postalCode) errors.postalCode = "Cannot be blank";

      if (!isValidNumber(postalCode))
        errors.postalCode = "Can only contain numbers";
    }

    return errors;
  };

  const handleSubmit = async ({
    name,
    bio,
    subDistrict,
    district,
    province,
    postalCode,
    streetAddress,
    apt,
  }) => {
    try {
      await patchCompany(company._id, {
        name,
        bio,
        address: {
          streetAddress,
          apt,
          subDistrict,
          district,
          province,
          postalCode,
        },
      });

      toast.success("Company info saved");
      navigate("/companies");
    } catch (error) {
      toast.success("Unable to update");
    }
  };

  const handleChangePlanClick = () => {
    // * Check if is company
    navigate("/change-plan");
  };

  const handleLeaveCompany = async () => {
    const _guestPlan = plans.find((p) => p.name === "Guest");

    await patchCompany(company._id, {
      plan: _guestPlan._id,
      roles: [],
      company: null,
    });

    refetchUser();

    navigate("/profile");
  };

  if (pageState === PAGE_STATE.LOADING) {
    return <></>;
  }

  const { name, surname, bio, address } = company;

  const isLeaveCompany = company.company && currentPlan.name === "Business";

  return (
    <div className="profile-edit-profile">
      <div className="profile-edit-profile-avatar-section">
        <img
          className="profile-edit-profile-avatar-section__avatar"
          src={`${assetsUri}/${avatar}`}
          alt="avatar"
          onError={({ target }) => {
            target.src = avatar;
          }}
        />
        <Button
          id="avatar"
          label={Translation("Upload picture")}
          onClick={() => setToggleModal(true)}
        />
      </div>
      <div className="profile-edit-profile-info-section">
        <Formik
          enableReinitialize={true}
          initialValues={{
            name,
            bio,
            streetAddress: address?.streetAddress || "",
            apt: address?.apt || "",
            subDistrict: address?.subDistrict || "",
            district: address?.district || "",
            province: address?.province || "",
            postalCode: address?.postalCode || "",
          }}
          validate={handleValidate}
          onSubmit={handleSubmit}
        >
          {({
            values: { plan, email, googleId, facebookId, lineId, wechatId },
          }) => (
            <Form>
              <div
                className="profile-edit-profile-info-section__section profile-edit-profile-info-section-info"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <h3 className="profile-edit-profile-info-section-header">
                  {Translation("Information")}
                </h3>
                <TextBox name="name" label={Translation("Name")} />
                <TextArea
                  name="bio"
                  label={Translation("Bio")}
                  placeholder={Translation("Enter you bio")}
                />
              </div>
              <div className="profile-edit-profile-info-section__section profile-edit-profile-info-section-address">
                <h3 className="profile-edit-profile-info-section-header">
                  {Translation("Address")}
                </h3>
                <TextBox
                  name="streetAddress"
                  label={Translation("Street Address")}
                  className="profile-edit-profile-info-section-address__street-address"
                />
                <TextBox
                  name="apt"
                  label={"Apt, suite, etc (Optional)"}
                  className="profile-edit-profile-info-section-address__apt"
                />
                <TextBox
                  name="subDistrict"
                  label={Translation("Sub-District")}
                />
                <TextBox name="district" label={Translation("District")} />
                <TextBox name="province" label={Translation("Province")} />
                <TextBox
                  name="postalCode"
                  label={Translation("Postal code")}
                  type="number"
                  autoComplete="new-zipcode"
                />
              </div>
              <div className="profile-edit-profile-info-section-cta">
                <Button label={TranslationCommon("Save")} type="submit" />
                <Button
                  variant="secondary"
                  label={TranslationCommon("Cancel")}
                  onClick={handleCancelClick}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
      {toggleModal && (
        <ModalUpload
          aspectRatio={1}
          onClose={handleViewClose}
          onSuccess={handleFileChange}
          circularCrop={true}
        />
      )}
    </div>
  );
};

EditCompany.propTypes = {};

export default EditCompany;
